import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

export interface UserInfo {
  name: string;
  email: string;
}

interface UseAIConciergeReturn {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
  saveConversation: () => Promise<void>;
}

const CHAT_URL = "https://itcjwpvspbzbojqyiiqn.supabase.co/functions/v1/ai-concierge";

export const useAIConcierge = (userInfo: UserInfo | null): UseAIConciergeReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced save after each assistant response
  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveConversation();
    }, 3000); // Save 3 seconds after last message
  }, []);

  // Save conversation to contact_messages
  const saveConversation = useCallback(async () => {
    if (!userInfo || messages.length < 2) return;

    try {
      // Create summary from messages
      const summary = messages
        .map((m) => `${m.role === "user" ? "User" : "AI"}: ${m.content}`)
        .join("\n\n");

      // Prepare conversation data
      const conversationData = messages.map((m) => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp.toISOString(),
      }));

      const { error: upsertError } = await supabase
        .from("contact_messages")
        .upsert(
          {
            id: sessionIdRef.current,
            name: userInfo.name,
            email: userInfo.email,
            message: summary,
            source: "ai_concierge",
            session_id: sessionIdRef.current,
            conversation_data: conversationData,
          },
          { onConflict: "id" }
        );

      if (upsertError) {
        console.error("Failed to save conversation:", upsertError);
      } else {
        console.log("Conversation saved successfully");
      }
    } catch (err) {
      console.error("Error saving conversation:", err);
    }
  }, [userInfo, messages]);

  // Save on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (userInfo && messages.length >= 2) {
        // Use sendBeacon for reliable unload saving
        const summary = messages
          .map((m) => `${m.role === "user" ? "User" : "AI"}: ${m.content}`)
          .join("\n\n");

        const conversationData = messages.map((m) => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp.toISOString(),
        }));

        const payload = {
          id: sessionIdRef.current,
          name: userInfo.name,
          email: userInfo.email,
          message: summary,
          source: "ai_concierge",
          session_id: sessionIdRef.current,
          conversation_data: conversationData,
        };

        navigator.sendBeacon(
          "https://itcjwpvspbzbojqyiiqn.supabase.co/rest/v1/contact_messages",
          JSON.stringify(payload)
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [userInfo, messages]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      setError(null);
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: content.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      // Prepare messages for API (only role and content)
      const apiMessages = [...messages, userMessage].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      let assistantContent = "";
      const assistantId = crypto.randomUUID();

      try {
        abortControllerRef.current = new AbortController();

        const response = await fetch(CHAT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml0Y2p3cHZzcGJ6Ym9qcXlpaXFuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU2MTkwNDcsImV4cCI6MjA3MTE5NTA0N30.qbMa2k-8ncKQCpV6WHQ0-cS4aMOMUeTsdHMjjryeBAc`,
          },
          body: JSON.stringify({ 
            messages: apiMessages,
            userInfo: userInfo,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 429) {
            throw new Error("Too many messages. Please wait a moment and try again.");
          }
          if (response.status === 402) {
            throw new Error("Service temporarily unavailable. Please try again later.");
          }
          throw new Error(errorData.error || "Failed to get response");
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let textBuffer = "";
        let streamDone = false;

        // Create initial assistant message
        setMessages((prev) => [
          ...prev,
          {
            id: assistantId,
            role: "assistant",
            content: "",
            timestamp: new Date(),
          },
        ]);

        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) break;

          textBuffer += decoder.decode(value, { stream: true });

          let newlineIndex: number;
          while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
            let line = textBuffer.slice(0, newlineIndex);
            textBuffer = textBuffer.slice(newlineIndex + 1);

            if (line.endsWith("\r")) line = line.slice(0, -1);
            if (line.startsWith(":") || line.trim() === "") continue;
            if (!line.startsWith("data: ")) continue;

            const jsonStr = line.slice(6).trim();
            if (jsonStr === "[DONE]") {
              streamDone = true;
              break;
            }

            try {
              const parsed = JSON.parse(jsonStr);
              const deltaContent = parsed.choices?.[0]?.delta?.content as string | undefined;
              if (deltaContent) {
                assistantContent += deltaContent;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId ? { ...m, content: assistantContent } : m
                  )
                );
              }
            } catch {
              // Incomplete JSON, put back and wait for more
              textBuffer = line + "\n" + textBuffer;
              break;
            }
          }
        }

        // Final buffer flush
        if (textBuffer.trim()) {
          for (let raw of textBuffer.split("\n")) {
            if (!raw) continue;
            if (raw.endsWith("\r")) raw = raw.slice(0, -1);
            if (raw.startsWith(":") || raw.trim() === "") continue;
            if (!raw.startsWith("data: ")) continue;
            const jsonStr = raw.slice(6).trim();
            if (jsonStr === "[DONE]") continue;
            try {
              const parsed = JSON.parse(jsonStr);
              const deltaContent = parsed.choices?.[0]?.delta?.content as string | undefined;
              if (deltaContent) {
                assistantContent += deltaContent;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId ? { ...m, content: assistantContent } : m
                  )
                );
              }
            } catch {
              /* ignore */
            }
          }
        }

        // Trigger debounced save after assistant responds
        debouncedSave();
      } catch (err) {
        if ((err as Error).name === "AbortError") {
          return;
        }
        console.error("AI Concierge error:", err);
        setError(err instanceof Error ? err.message : "Something went wrong");
        // Remove the empty assistant message on error
        setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [messages, isLoading, userInfo, debouncedSave]
  );

  const clearMessages = useCallback(() => {
    // Save conversation before clearing if there are messages
    if (messages.length >= 2) {
      saveConversation();
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setMessages([]);
    setError(null);
    setIsLoading(false);
    // Generate new session ID for next conversation
    sessionIdRef.current = crypto.randomUUID();
  }, [messages, saveConversation]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    saveConversation,
  };
};
